'use client';

import { useTranslations } from 'next-intl';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlanDialogScene } from '@/app/components/shared/PlanDetailDialog';
import { TaskSubmitButton } from '@/app/components/shared/TaskSubmitButton';
import { ELEMENT_NO_VOICE_ID } from '@/app/components/shared/VoiceSelectionDialog';
import { showTaskSubmitSuccessToast } from '@/app/components/ui/task-submit-success-toast';
import { Textarea } from '@/app/components/ui/textarea';
import { toast } from '@/app/components/ui/use-toast';
import {
  useAdsDialog,
  useCredits,
  useElementErrorMsg,
  useFetch,
  useFreeCreditsTasks,
  useGlobalModal,
  useMembershipRevision,
  useTrials,
} from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { reportEvent } from '@/app/utils';
import { ToolFormContainer } from '../_components/ToolFormContainer';
import { useToolForm } from '../_hooks/use-tool-form';
import { StyleSelector } from '../style-transfer/_components/StyleSelector';
import { CharacterCreationSettings, CharacterElementList, CharacterInfoConfig } from './_components';
import { useCharacterElements, useCharacterElementTaskSse } from './_hooks';
import { getElementTaskCost, materialsCancelElementTask, materialsCreateElementTask } from './apis';
import type {
  CharacterCreationRequest,
  CharacterElementTag,
  CharacterElementTaskHistory,
  CharacterElementTaskStateReply,
} from './types';

const CharacterCreationForm: FC = () => {
  const t = useTranslations();
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const { open: openPurchaseCreditsDialog } = useGlobalModal('purchaseCreditsDialog');
  const isNewMembership = useMembershipRevision();
  const { tryOpenInsufficientCreditsDialog } = useAdsDialog();
  const { getErrorMsg } = useElementErrorMsg({
    ElementExists: t('tools.characterCreation.error.nameDuplicate'),
  });
  const { settings, updateSettings } = useToolForm('character_creation');
  const elementListRef = useRef<HTMLDivElement | null>(null);
  const {
    tasks,
    loading: elementsLoading,
    loadingMore: elementsLoadingMore,
    loadMore: loadMoreElements,
    noMore: noMoreElements,
    refreshElements,
    addTask,
    activeTaskId,
    handleTaskState,
    markTaskCanceled,
    updateTaskElement,
  } = useCharacterElements(elementListRef);
  const syncedTerminalTaskIdsRef = useRef<Set<string>>(new Set());
  const { updateCredits } = useCredits();
  const { updateTrialsAsync } = useTrials();
  const { fetchFreeCreditTasks } = useFreeCreditsTasks();

  const {
    updateCharacterCreationFormParams,
    textPrompt,
    userInfo,
    creditsInfo,
    isTeamSpace,
    invalidateUserQuota,
    referenceModalUpdateReason,
  } = useStore((state) => ({
    updateCharacterCreationFormParams: state.updateCharacterCreationFormParams,
    textPrompt: state.characterCreationFormParams.textPrompt,
    userInfo: state.userInfo,
    creditsInfo: state.creditsInfo,
    isTeamSpace: state.isTeamSpace,
    invalidateUserQuota: state.invalidateUserQuota,
    referenceModalUpdateReason: state.referenceModalUpdateReason,
  }));

  const [characterName, setCharacterName] = useState('');
  const [characterTag, setCharacterTag] = useState<CharacterElementTag>('role');
  const [characterVoiceId, setCharacterVoiceId] = useState<string | undefined>(ELEMENT_NO_VOICE_ID);

  const refreshUsageState = useCallback(() => {
    updateCredits();
    updateTrialsAsync().then(() => {
      fetchFreeCreditTasks();
    });
    invalidateUserQuota();
  }, [fetchFreeCreditTasks, invalidateUserQuota, updateCredits, updateTrialsAsync]);

  const { run: createElementTask, loading: submitting } = useFetch(
    (body: CharacterCreationRequest) =>
      materialsCreateElementTask({
        body,
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          toast({
            type: 'error',
            message: getErrorMsg(error, t('popup.toast.networkError')),
          });
          return;
        }

        if (!data?.task_id) {
          toast({
            type: 'error',
            message: t('popup.toast.networkError'),
          });
          return;
        }

        addTask({
          id: data.task_id,
          state: 'created',
          prompt: submitValue.prompt,
          sample_count: Number(settings.sample_count) || 1,
          completed_count: 0,
          created_at: new Date().toISOString(),
        });
        refreshUsageState();
        showTaskSubmitSuccessToast(t('popup.toast.taskSubmitSuccess'));
        reportEvent('create_video_click', {
          task_type: 'character_creation',
          is_success: true,
          task_id: data.task_id,
        });
      },
    },
  );

  const { data: elementTaskCostRes } = useFetch(() => getElementTaskCost(), {
    refreshDeps: [userInfo?.id],
    suppressUnauthorizedLoginDialog: true,
  });

  const { run: cancelElementTask } = useFetch(
    (taskId: string) =>
      materialsCancelElementTask({
        path: {
          task_id: taskId,
        },
        body: {
          task_id: taskId,
        },
      }),
    {
      manual: true,
      onSuccess: ({ error }, [taskId]) => {
        if (error) {
          toast({
            type: 'warning',
            message: t('popup.toast.cancelFailedOther'),
          });
          return;
        }
        toast({
          type: 'success',
          message: t('popup.toast.cancelSuccess'),
          duration: 3000,
        });
        refreshUsageState();
        markTaskCanceled(taskId);
      },
      onError: () => {
        toast({
          type: 'warning',
          message: t('popup.toast.cancelFailedOther'),
        });
      },
    },
  );

  const [costCreditsPerSample, originalCreditsPerSample] = useMemo(() => {
    const cost = elementTaskCostRes?.data?.cost_credits;
    const original = elementTaskCostRes?.data?.original_cost_credits;
    if (original !== undefined && cost !== undefined && original > cost) {
      return [cost, original] as const;
    }
    return [cost, undefined] as const;
  }, [elementTaskCostRes]);

  const sampleCount = Number(settings.sample_count) || 1;

  const costCredits = useMemo(() => {
    if (costCreditsPerSample === undefined) return undefined;
    return costCreditsPerSample * sampleCount;
  }, [costCreditsPerSample, sampleCount]);

  const originalCredits = useMemo(() => {
    if (originalCreditsPerSample === undefined) return undefined;
    return originalCreditsPerSample * sampleCount;
  }, [originalCreditsPerSample, sampleCount]);

  const creditsInsufficient = useMemo(
    () => !!(costCredits && creditsInfo && creditsInfo.total < costCredits),
    [costCredits, creditsInfo],
  );

  const handleTextPromptChange = useCallback(
    (value: string) => {
      updateCharacterCreationFormParams('textPrompt', value);
    },
    [updateCharacterCreationFormParams],
  );

  const syncUsageState = useCallback(
    (taskId: string) => {
      if (syncedTerminalTaskIdsRef.current.has(taskId)) return;
      syncedTerminalTaskIdsRef.current.add(taskId);
      refreshUsageState();
    },
    [refreshUsageState],
  );

  const handleElementTaskState = useCallback(
    (state: CharacterElementTaskStateReply) => {
      handleTaskState(state);
      if (state.task_id && (state.state === 'success' || state.state === 'failed' || state.state === 'canceled')) {
        syncUsageState(state.task_id);
      }
    },
    [handleTaskState, syncUsageState],
  );

  const submitValue = useMemo<CharacterCreationRequest>(
    () => ({
      prompt: textPrompt?.trim() || '',
      name: characterName.trim(),
      style: (settings.style as string | undefined) || undefined,
      tag: characterTag,
      sample_count: sampleCount,
      voice_id: characterTag === 'role' ? characterVoiceId : ELEMENT_NO_VOICE_ID,
    }),
    [characterName, characterTag, characterVoiceId, sampleCount, settings.style, textPrompt],
  );

  const submitDisabledTip = useMemo(() => {
    if (!textPrompt?.trim()) return t('tools.characterCreation.error.promptRequired');
    if (!characterName.trim()) return t('tools.characterCreation.error.nameRequired');
    if (!settings.style) return t('tools.characterCreation.error.styleRequired');
    return undefined;
  }, [characterName, settings.style, t, textPrompt]);

  const handleCreditsInsufficientClick = useCallback(() => {
    if (isTeamSpace) {
      openConfirmDialog({
        title: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.title'),
        description: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.content'),
        primaryButtonText: t('popup.dialog.error.confirm'),
      });
      return;
    }

    if (userInfo?.subs_plan === 'free' && !isNewMembership) {
      openPlanDetailDialog({ scene: PlanDialogScene.InsufficientCredits });
      return;
    }

    openPurchaseCreditsDialog({
      showTriggerButton: false,
      onClose: () => {
        tryOpenInsufficientCreditsDialog();
      },
    });
    reportEvent('purchase_credits_exposure', { from_position: 'character_creation -null' });
  }, [
    isNewMembership,
    isTeamSpace,
    openConfirmDialog,
    openPlanDetailDialog,
    openPurchaseCreditsDialog,
    t,
    tryOpenInsufficientCreditsDialog,
    userInfo?.subs_plan,
  ]);

  const handleSubmit = useCallback(() => {
    if (!userInfo) {
      openLoginDialog();
      reportEvent('login_pop_exposure', {
        refer_element: 'character creation - submit',
      });
      return;
    }

    if (submitDisabledTip) {
      toast({
        type: 'warning',
        message: submitDisabledTip,
      });
      return;
    }

    if (creditsInsufficient) {
      handleCreditsInsufficientClick();
      return;
    }

    createElementTask(submitValue);
  }, [
    createElementTask,
    creditsInsufficient,
    handleCreditsInsufficientClick,
    openLoginDialog,
    submitDisabledTip,
    submitValue,
    userInfo,
  ]);

  const handleReEditTask = useCallback(
    (task: CharacterElementTaskHistory) => {
      const tag = task.tag && task.tag !== 'unspecified' ? task.tag : 'role';
      handleTextPromptChange(task.prompt ?? '');
      setCharacterName(task.name ?? '');
      setCharacterTag(tag);
      setCharacterVoiceId(tag === 'role' ? task.voice_id || ELEMENT_NO_VOICE_ID : ELEMENT_NO_VOICE_ID);
      updateSettings({
        sample_count: task.sample_count || 1,
        style: task.style || '',
      });
      elementListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      reportEvent('recreate_click', {
        task_type: 'character_creation',
        task_id: task.id || '',
        interaction_position: 'character_creation_task_history',
      });
    },
    [handleTextPromptChange, updateSettings],
  );

  useCharacterElementTaskSse({
    taskId: activeTaskId,
    enabled: Boolean(activeTaskId),
    onState: handleElementTaskState,
  });

  useEffect(() => {
    if (referenceModalUpdateReason?.type !== 'update') return;

    updateTaskElement(referenceModalUpdateReason.reference);
  }, [referenceModalUpdateReason, updateTaskElement]);

  return (
    <div className="mx-auto flex h-full w-full gap-5 px-6 pb-6 sm:flex-col sm:px-3">
      <div className="relative w-[37%] max-w-[520px] sm:w-full sm:min-w-[334px] md:max-w-full lg:min-w-[384px]">
        <div id="tool-container" className="hide-scrollbar flex h-full w-full flex-col overflow-y-scroll pb-[72px]">
          <ToolFormContainer
            title={t('tools.characterCreation.title')}
            description={t('tools.characterCreation.description')}
            className="px-2"
            mediaSection={
              <div className="flex w-full flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <span className="font-medium light:text-system-black text-sm text-system-white leading-[1.4]">
                    {t('tools.characterCreation.prompt.label')}
                  </span>
                  <Textarea
                    value={textPrompt || ''}
                    onChange={(event) => handleTextPromptChange(event.target.value)}
                    placeholder={t('tools.characterCreation.prompt.placeholder')}
                    maxLength={1000}
                    rows={4}
                    className="min-h-[112px] w-full resize-none rounded-10 border-system-white08 light:border-system-black08 bg-system-white04 light:bg-system-black04 px-3 py-2.5 light:text-system-black text-sm text-system-white light:placeholder:text-system-black/40 placeholder:text-system-white/40"
                  />
                </div>

                <StyleSelector
                  selectedStyle={settings.style as string | undefined}
                  onStyleChange={(style) => updateSettings({ style: style?.id })}
                  autoSelectFirstOption
                  showTextPrompt={false}
                  mobileColumnCount={3}
                />

                <CharacterInfoConfig
                  name={characterName}
                  tag={characterTag}
                  voiceId={characterVoiceId}
                  onNameChange={setCharacterName}
                  onTagChange={setCharacterTag}
                  onVoiceIdChange={setCharacterVoiceId}
                />

                <CharacterCreationSettings
                  sampleCount={sampleCount}
                  onSampleCountChange={(value) => updateSettings({ sample_count: value })}
                />
              </div>
            }
            submitButton={
              <TaskSubmitButton
                disabled={Boolean(submitDisabledTip)}
                disabledTip={submitDisabledTip}
                submitting={submitting}
                costCredits={costCredits}
                originalCredits={originalCredits}
                creditsInsufficient={creditsInsufficient}
                onClick={creditsInsufficient ? handleCreditsInsufficientClick : handleSubmit}
                className="w-full max-w-[360px] rounded-12"
              >
                {t('create.createForm.submit')}
              </TaskSubmitButton>
            }
          />
        </div>
      </div>

      <div
        ref={elementListRef}
        className="min-w-0 flex-1 overflow-hidden rounded-16 border border-system-white06 light:border-system-black08 bg-[#191B20] light:bg-system-black04 p-5 sm:min-h-[480px] sm:p-4"
      >
        <CharacterElementList
          tasks={tasks}
          loading={elementsLoading}
          loadingMore={elementsLoadingMore}
          noMore={noMoreElements}
          onRefresh={refreshElements}
          onLoadMore={loadMoreElements}
          onCancelTask={cancelElementTask}
          onReEditTask={handleReEditTask}
        />
      </div>
    </div>
  );
};

export default CharacterCreationForm;
